import React, { useEffect, useRef } from 'react';
import { BRAND_COLORS } from './constants';

// Version 3: Multi-Snake Battle Background (Enhanced)
export const SnakeBackground: React.FC = () => {
    const canvasRef = useRef<HTMLCanvasElement>(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const gridSize = 20; // Same as V4 for consistency

        interface Food {
            x: number;
            y: number;
            type: 'normal' | 'super';
            glow: number;
        }

        interface Snake {
            body: Array<{ x: number; y: number }>;
            direction: { x: number; y: number };
            color: string;
            glowColor: string;
            speed: number;
            lastMoveTime: number;
            target?: Food;
            id: string;
        }

        let snakes: Snake[] = [];
        let food: Food[] = [];

        // Create color variations using brand colors
        const snakeColors = [
            { main: BRAND_COLORS.primary, glow: '#4C51BF' },
            { main: BRAND_COLORS.secondary, glow: '#4299E1' },
            { main: '#805AD5', glow: '#B794F6' }, // Purple variant
            { main: '#38B2AC', glow: '#81E6D9' }, // Teal variant
            { main: '#ED8936', glow: '#F6AD55' }, // Orange variant
            { main: '#E53E3E', glow: '#FC8181' }, // Red variant
        ];

        const getRandomFood = (): Food => {
            return {
                x: Math.floor(Math.random() * (canvas.width / gridSize)),
                y: Math.floor(Math.random() * (canvas.height / gridSize)),
                type: Math.random() < 0.2 ? 'super' : 'normal',
                glow: 0
            };
        };

        const createSnake = (index: number): Snake => {
            const colorScheme = snakeColors[index % snakeColors.length];
            const startX = Math.floor(Math.random() * (canvas.width / gridSize));
            const startY = Math.floor(Math.random() * (canvas.height / gridSize));

            return {
                body: [{ x: startX, y: startY }],
                direction: { x: 1, y: 0 },
                color: colorScheme.main,
                glowColor: colorScheme.glow,
                speed: 150 + Math.random() * 100, // Varied speeds
                lastMoveTime: 0,
                id: `snake_${index}`
            };
        };

        const initializeGame = () => {
            snakes = [];
            food = [];

            // Create 4-6 snakes
            const numSnakes = 4 + Math.floor(Math.random() * 3);
            for (let i = 0; i < numSnakes; i++) {
                snakes.push(createSnake(i));
            }

            // Create 8-15 food items
            const numFood = 8 + Math.floor(Math.random() * 8);
            for (let i = 0; i < numFood; i++) {
                food.push(getRandomFood());
            }
        };

        const resizeCanvas = () => {
            canvas.width = window.innerWidth;
            canvas.height = window.innerHeight;
            initializeGame();
        };

        resizeCanvas();
        window.addEventListener('resize', resizeCanvas);

        const findNearestFood = (snake: Snake): Food | undefined => {
            if (food.length === 0) return undefined;

            const head = snake.body[0];
            return food.reduce((nearest, currentFood) => {
                const currentDist = Math.abs(head.x - currentFood.x) + Math.abs(head.y - currentFood.y);
                const nearestDist = Math.abs(head.x - nearest.x) + Math.abs(head.y - nearest.y);
                return currentDist < nearestDist ? currentFood : nearest;
            });
        };

        const updateSnakeDirection = (snake: Snake) => {
            // AI to target nearest food
            const target = findNearestFood(snake);
            if (!target) return;

            const head = snake.body[0];
            const dx = target.x - head.x;
            const dy = target.y - head.y;

            // Simple AI - move towards food, but add some randomness
            if (Math.random() < 0.8) { // 80% smart movement
                if (Math.abs(dx) > Math.abs(dy)) {
                    snake.direction = { x: dx > 0 ? 1 : -1, y: 0 };
                } else {
                    snake.direction = { x: 0, y: dy > 0 ? 1 : -1 };
                }
            } else { // 20% random movement
                const directions = [
                    { x: 1, y: 0 }, { x: -1, y: 0 }, { x: 0, y: 1 }, { x: 0, y: -1 }
                ];
                snake.direction = directions[Math.floor(Math.random() * directions.length)];
            }
        };

        let animationFrameId: number;

        const gameLoop = (now: number) => {
            ctx.fillStyle = BRAND_COLORS.background;
            ctx.fillRect(0, 0, canvas.width, canvas.height);

            // Update and draw snakes
            snakes.forEach((snake, snakeIndex) => {
                // Move snake based on its individual speed
                if (now - snake.lastMoveTime >= snake.speed) {
                    // Update direction occasionally
                    if (Math.random() < 0.1) {
                        updateSnakeDirection(snake);
                    }

                    // Move snake
                    const head = {
                        x: snake.body[0].x + snake.direction.x,
                        y: snake.body[0].y + snake.direction.y
                    };

                    // Wrap around edges
                    if (head.x >= canvas.width / gridSize) head.x = 0;
                    if (head.x < 0) head.x = canvas.width / gridSize - 1;
                    if (head.y >= canvas.height / gridSize) head.y = 0;
                    if (head.y < 0) head.y = canvas.height / gridSize - 1;

                    snake.body.unshift(head);

                    // Check for food collision
                    let foodEaten = false;
                    food.forEach((foodItem, foodIndex) => {
                        if (head.x === foodItem.x && head.y === foodItem.y) {
                            // Snake grows, food disappears
                            foodEaten = true;
                            food.splice(foodIndex, 1);

                            // Add new food to maintain chaos
                            if (Math.random() < 0.7) {
                                food.push(getRandomFood());
                            }

                            // Super food makes snake grow more
                            if (foodItem.type === 'super') {
                                // Don't pop tail for extra growth
                            } else {
                                // Normal food - still grow by not popping
                            }
                        }
                    });

                    if (!foodEaten) {
                        snake.body.pop();
                    }

                    // Limit snake length to prevent taking over screen
                    if (snake.body.length > 15) {
                        snake.body = snake.body.slice(0, 12);
                    }

                    snake.lastMoveTime = now;
                }

                // Draw snake with glow effect
                snake.body.forEach((segment, segmentIndex) => {
                    const opacity = segmentIndex === 0 ? '60' : '40'; // Head is brighter

                    // Glow effect
                    ctx.fillStyle = `${snake.glowColor}20`;
                    ctx.fillRect(
                        segment.x * gridSize - 2,
                        segment.y * gridSize - 2,
                        gridSize + 4,
                        gridSize + 4
                    );

                    // Main body
                    ctx.fillStyle = `${snake.color}${opacity}`;
                    ctx.fillRect(
                        segment.x * gridSize,
                        segment.y * gridSize,
                        gridSize - 2,
                        gridSize - 2
                    );
                });
            });

            // Update and draw food with glow effect
            food.forEach(foodItem => {
                foodItem.glow += 0.1;
                const glowIntensity = Math.sin(foodItem.glow) * 0.3 + 0.7;

                const foodColor = foodItem.type === 'super' ? BRAND_COLORS.primary : BRAND_COLORS.secondary;

                // Glow effect
                ctx.fillStyle = `${foodColor}${Math.floor(glowIntensity * 80).toString(16).padStart(2, '0')}`;
                ctx.fillRect(
                    foodItem.x * gridSize - 3,
                    foodItem.y * gridSize - 3,
                    gridSize + 6,
                    gridSize + 6
                );

                // Main food
                const opacity = foodItem.type === 'super' ? '80' : '60';
                ctx.fillStyle = `${foodColor}${opacity}`;
                ctx.fillRect(
                    foodItem.x * gridSize,
                    foodItem.y * gridSize,
                    gridSize - 2,
                    gridSize - 2
                );
            });

            // Occasionally add more food for chaos
            if (Math.random() < 0.02 && food.length < 20) {
                food.push(getRandomFood());
            }

            // Remove snakes that get too long and add new ones
            if (Math.random() < 0.005) {
                const longSnakes = snakes.filter(s => s.body.length > 10);
                if (longSnakes.length > 0 && snakes.length > 2) {
                    const removeIndex = snakes.indexOf(longSnakes[0]);
                    snakes.splice(removeIndex, 1);

                    // Add a new snake
                    if (snakes.length < 6) {
                        snakes.push(createSnake(snakes.length));
                    }
                }
            }

            animationFrameId = requestAnimationFrame(gameLoop);
        };

        initializeGame();
        animationFrameId = requestAnimationFrame(gameLoop);

        return () => {
            window.removeEventListener('resize', resizeCanvas);
            cancelAnimationFrame(animationFrameId);
        };
    }, []);

    return (
        <canvas
            ref={canvasRef}
            className="fixed inset-0 pointer-events-none z-0"
            style={{ background: BRAND_COLORS.background }}
        />
    );
};
